import { isNumber } from 'lodash';
import { useEffect, useState } from 'react';
import { InputNumber, SelectAngle, SliderInput } from '@meitu/saas-ui';
import { hexToHsva, hsvaToHexa } from '@meitu/saas-colors/dist/esm/utils/color-convert';
import { type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import { translateAngleToXY, translateLongSide, translateXYToAngle } from '@meitu/saas-editor-core/es/utils/math-kit';
import { LayerType, type IFontShadow, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { EffectPropsContainer } from '@/components/effect-props-container';
import { EffectPropsGroup } from '@/components/effect-props-group';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useLocaleStore } from '@/i18n';
import { useLayerProps } from '../../hooks';
import { type WidgetCommonProps } from '../../types';
import { Tracker } from '../../utils';
import './style.less';

type Props = WidgetCommonProps;

const FALLBACK_COLOR = '#000000ff';

export const ShadowInspector: React.FC<Props> = ({ layerId, recordId }) => {
    const { $t } = useLocaleStore();

    const editor = useEditor();
    const shadow = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const textShadow = layerData.textShadow || [];

        return textShadow.length > 0 ? textShadow[0] : null;
    }) as IFontShadow | null;

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const hidden = shadow ? Boolean(shadow.hidden) : true;
    const color = shadow ? shadow.color : FALLBACK_COLOR;
    const hsva = hexToHsva(color);
    const opacity = Math.round(hsva.a * 100);
    const angle = shadow ? translateXYToAngle(shadow.x, shadow.y) : 0;
    const blur = shadow ? shadow.blur : 0;
    const distance = shadow ? translateLongSide(shadow.x, shadow.y) : 0;

    // 由于 angle、distance 都是由 x、y 属性计算而来
    // 所以在修改某一项值的时候，都有可能影响到另外一项数据
    // 所以在组件内单独维护 angle、distance 的状态
    const [angleValue, setAngleValue] = useState(angle);
    const [distanceValue, setDistanceValue] = useState(distance);

    const add = async () => {
        if (shadow) return;
        const layer = await editor.getInstance<TextLayer>('TextLayer').addShadow(layerId);

        if (layer) {
            // ATTENTION: 文本支持批量操作，返回的 layer 可能是组图层
            const layerData =
                layer.layerType === LayerType.GROUP_LAYER
                    ? (layer.children[0].data as ITextLayerProps)
                    : (layer.data as ITextLayerProps);

            if (!layerData) return;

            const textShadow = layerData.textShadow[0];

            if (textShadow) {
                const initialAngle = Number(translateXYToAngle(textShadow.x, textShadow.y).toFixed(1));
                const initialDistance = Number(translateLongSide(textShadow.x, textShadow.y).toFixed(1));
                setAngleValue(initialAngle);
                setDistanceValue(initialDistance);
            }
        }

        Tracker.addShadow({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'secondary',
        });
    };

    const remove = () => {
        if (!shadow) return;
        editor.getInstance<TextLayer>('TextLayer').removeEffect(layerId, 'textShadow');

        Tracker.removeShadow({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'secondary',
        });
    };

    const setVisibility = (flag: boolean) => {
        if (!shadow) return;
        editor.getInstance<TextLayer>('TextLayer').setEffectVisibility(layerId, 'textShadow', flag);

        Tracker.toggleShadow({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'secondary',
        });
    };

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openColorPicker({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'secondary',

                kind: 'textShadow',
            });
        }
    };

    const adjustColor = (value: string, options = { writeHistory: true }) => {
        if (!shadow) return;

        const payload = { idx: 0, data: { color: value } };
        editor.getInstance<TextLayer>('TextLayer').adjustShadowProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustShadowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'secondary',

                prop: 'color',
                value,
            });
        }
    };

    // FIXME: distance 为 0 时，调整 angle 没有意义
    // 此时应该在 UI 上禁用，已和产品反馈，在下一个迭代中处理
    const adjustAngle = (value: number, options = { writeHistory: true }) => {
        if (!shadow || !isNumber(value)) return;

        if (distanceValue === 0) {
            setAngleValue(value);
            return;
        }

        const { x, y } = translateAngleToXY(value, distanceValue);
        const payload = { idx: 0, data: { x, y } };

        editor.getInstance<TextLayer>('TextLayer').adjustShadowProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustShadowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'secondary',

                prop: 'angle',
                value,
            });
        }
    };

    const adjustBlur = (value: number, options = { writeHistory: true }) => {
        if (!shadow || !isNumber(value)) return;

        const payload = { idx: 0, data: { blur: value } };

        editor.getInstance<TextLayer>('TextLayer').adjustShadowProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustShadowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'secondary',

                prop: 'blur',
                value,
            });
        }
    };

    const adjustDistance = (value: number, options = { writeHistory: true }) => {
        if (!shadow || !isNumber(value)) return;

        const { x, y } = translateAngleToXY(angleValue, value);
        const payload = { idx: 0, data: { x, y } };

        editor.getInstance<TextLayer>('TextLayer').adjustShadowProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustShadowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'secondary',

                prop: 'distance',
                value,
            });
        }
    };

    const adjustOpacity = (value: number, options = { writeHistory: true }) => {
        if (!shadow || !isNumber(value)) return;

        const opacity = Math.round(value);
        const hsva = hexToHsva(color);
        const nextColor = hsvaToHexa({ ...hsva, a: opacity / 100 });

        const payload = { idx: 0, data: { color: nextColor } };
        editor.getInstance<TextLayer>('TextLayer').adjustShadowProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustShadowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'secondary',

                prop: 'opacity',
                value,
            });
        }
    };

    // 由于 angleValue、distanceValue 是 UI 层单独维护的状态
    // 所以需要根据 angle、distance 的变化进行同步
    useEffect(() => {
        setAngleValue(angle);
    }, [angle]);

    useEffect(() => {
        setDistanceValue(distance);
        if (distance === 0 && angleValue !== 0) {
            setAngleValue(angleValue);
        }
    }, [distance]);

    return (
        <EffectPropsContainer
            icon="DropshadowBold"
            title={$t((v) => v.textStyleAdd.shadow)}
            showAdd={shadow == null}
            visible={!hidden}
            onAdd={add}
            onRemove={remove}
            onToggleVisible={() => setVisibility(!hidden)}
        >
            {shadow ? (
                <div className="text-shadow-inspector">
                    <EffectPropsGroup disabled={hidden}>
                        {/* 颜色、角度 */}
                        <LabelAction title={$t((v) => v.textStyleAdd.color)} align>
                            <div className="text-shadow-inspector__color">
                                <ColorGroup
                                    popupTriggerSize={{ width: 58, height: 26 }}
                                    color={color}
                                    setColor={(color) => adjustColor(color, { writeHistory: false })}
                                    setColorAfter={(color, _, { isBrandColor }) => {
                                        adjustColor(color, { writeHistory: true });

                                        if (isBrandColor) {
                                            window._stat_track?.('hb_layers_func_use', {
                                                func_id: '1_107',
                                            });
                                        }
                                    }}
                                    onOpenChange={handleColorPickerOpenChange}
                                    showIcon={false}
                                    popupOverlayClassName="effect-color-picker"
                                    placement="left"
                                    onToggleBizTab={(bizTab) => {
                                        if (bizTab === 'team') {
                                            window._stat_track?.('hb_layers_func_click', {
                                                func_id: '1_107',
                                            });
                                        }
                                    }}
                                />
                                <div className="text-shadow-inspector__angle">
                                    <SelectAngle
                                        angle={~~angleValue}
                                        showValue={false}
                                        onChange={(angle) => adjustAngle(angle, { writeHistory: false })}
                                        onAfterChange={(angle) => adjustAngle(angle, { writeHistory: true })}
                                    />
                                    <InputNumber
                                        value={~~angleValue}
                                        precision={1}
                                        min={-180}
                                        max={180}
                                        style={{
                                            width: 58,
                                            height: 26,
                                            marginLeft: 8,
                                        }}
                                        showStepBtn
                                        onChange={(angle) => {
                                            if (angle != null) adjustAngle(angle, { writeHistory: false });
                                        }}
                                        onChangeAfter={(angle) => {
                                            if (angle != null) adjustAngle(angle, { writeHistory: true });
                                        }}
                                    />
                                </div>
                            </div>
                        </LabelAction>

                        {/* 模糊 */}
                        <LabelAction title={$t((v) => v.textStyleAdd.blur)} align>
                            <SliderInput
                                showStepBtn
                                value={blur}
                                min={0}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustBlur(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustBlur(value, { writeHistory: true })}
                            />
                        </LabelAction>

                        {/* 距离 */}
                        <LabelAction title={$t((v) => v.textStyleAdd.distance)} align>
                            <SliderInput
                                showStepBtn
                                value={distanceValue}
                                min={0}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustDistance(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustDistance(value, { writeHistory: true })}
                            />
                        </LabelAction>

                        {/* 透明度 */}
                        <LabelAction title={$t((v) => v.textStyleAdd.opacity)} align>
                            <SliderInput
                                showStepBtn
                                value={opacity}
                                min={0}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustOpacity(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustOpacity(value, { writeHistory: true })}
                            />
                        </LabelAction>
                    </EffectPropsGroup>
                </div>
            ) : null}
        </EffectPropsContainer>
    );
};
